import React from "react";
import {
  alpha,
  CardMedia,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { parseDateToLgComun } from "../../../../helpers/dateTransform";
import Etiquin from "../../../../assets/images/Ethiquin/ethiquin_no_data.svg";
import { ParamChip } from "./ParamChip";
import { UserTooltipChip } from "../../../Report/All/Information/components/UserTooltipChip";
import { OutlineEye } from "../../../Icon";

/**
 * Translate HTTP methods to human-readable terms
 */
const translateMethod = (method, t) => {
  const methodUpper = method?.toUpperCase();
  const translations = {
    GET: t("Bitacora.view_action"),
    POST: t("Bitacora.modification_action"),
    PUT: t("Bitacora.modification_action"),
    PATCH: t("Bitacora.modification_action"),
    DELETE: t("Bitacora.deletion_action"),
  };
  return translations[methodUpper] || method;
};

/**
 * Get color for HTTP method
 */
const getMethodColor = (method) => {
  const colors = {
    GET: "info",
    POST: "success",
    PUT: "warning",
    PATCH: "warning",
    DELETE: "error",
  };
  return colors[method?.toUpperCase()] || "default";
};

/**
 * Check if method is a modification action
 */
const isModificationMethod = (method) => {
  return ["POST", "PUT", "PATCH", "DELETE"].includes(method?.toUpperCase());
};

export const BitacoraTable = ({ bitacoraList = [] }) => {
  const [t] = useTranslation("report");
  const theme = useTheme();
  // chip color
  const getChipColor = (key) => {
    switch (key) {
      case "solicitud":
        return "primary";
      case "limit":
        return "success";
      case "offset":
        return "warning";
      case "error":
        return "error";
      default:
        return "secondary";
    }
  };
  // Helper for rendering the “Folio” column in the table
  const hasFolio = bitacoraList.some((b) => Boolean(b.folio));
  // Table columns
  const columns = React.useMemo(() => {
    const baseColumns = [
      { id: "id", label: t("Bitacora.id"), align: "center", minWidth: 50 },
      { id: "event", label: t("Bitacora.event"), align: "left", minWidth: 160 },
      { id: "user", label: t("Bitacora.user"), align: "left", minWidth: 160 },
      { id: "date", label: t("Bitacora.date"), align: "left", minWidth: 140 },
      {
        id: "details",
        label: t("Bitacora.params"),
        align: "left",
        minWidth: 160,
      },
    ];
    if (hasFolio) {
      baseColumns.splice(4, 0, {
        id: "folio",
        label: t("Bitacora.folio"),
        align: "left",
        minWidth: 140,
      });
    }
    return baseColumns;
  }, [hasFolio, t]);
  // Table rows
  const rows = React.useMemo(() => {
    if (!Array.isArray(bitacoraList)) return [];
    return bitacoraList.map((b) => {
      const actionMethod = b.actions?.method || b.method || t("Bitacora.n_a");
      return {
        id: b.id,
        event: t(`Rutas.${b.path}`),
        user: b.user,
        date: parseDateToLgComun(b.created_at),
        folio: b.folio,
        rawActions: b.actions || {},
        actionTag: actionMethod,
      };
    });
  }, [bitacoraList, t]);

  const hasRows = rows.length > 0;
  const hasMoreThan15Rows = rows.length > 15;

  return (
    <TableContainer
      sx={{
        height: "63.33vh",
        overflowY: "auto",
        overflowX: { xs: "auto", xl: "hidden" },
        border: `1px solid ${alpha(theme.palette.blueGray50.main, 0.3)}`,
        borderTopLeftRadius: "6px",
        borderTopRightRadius: "6px",
      }}
    >
      {!hasRows ? (
        <Stack
          sx={{
            height: "50vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CardMedia
            component="img"
            image={Etiquin}
            alt="not_found"
            sx={{ height: "45%", width: "45%", objectFit: "contain" }}
          />
          <Typography
            variant="h5"
            textAlign="center"
            color="secondary"
            mt={1.5}
          >
            {t("ReportsList.no_results_found")}...
          </Typography>
        </Stack>
      ) : (
        <Table
          stickyHeader
          aria-label="bitacora table list"
          size={hasMoreThan15Rows ? "small" : "medium"}
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    minWidth: column.minWidth,
                    py: 1.25,
                    backgroundColor: theme.palette.blueGray50.main,
                  }}
                >
                  <Typography variant="h6">{column.label}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  "&:hover": {
                    boxShadow: theme.shadows[1],
                  },
                }}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align}
                    sx={{ maxWidth: 200, px: 0.5 }}
                  >
                    {col.id === "details"
                      ? (() => {
                          const actions = row.rawActions;
                          const method = actions?.method || actions?.solicitud;
                          const userId = actions?.user_id || actions?.data?.user_id;

                          // Collect detail entries excluding known fields
                          const excludedKeys = ["method", "solicitud", "user_id", "report_id", "page", "page_size", "folio"];
                          const detailEntries = [];

                          // Nested data object
                          if (actions?.data && typeof actions.data === "object") {
                            Object.entries(actions.data).forEach(([key, value]) => {
                              if (!excludedKeys.includes(key) && value !== "" && value !== null && value !== undefined) {
                                detailEntries.push({ label: key, value: typeof value === "object" ? JSON.stringify(value) : String(value) });
                              }
                            });
                          }

                          // Top-level fields
                          if (actions?.status) {
                            detailEntries.push({ label: t("Bitacora.status_action"), value: t(`status.${actions.status}`) || actions.status });
                          }
                          Object.entries(actions || {}).forEach(([key, value]) => {
                            if (!excludedKeys.includes(key) && !["status", "data"].includes(key) && typeof value !== "object") {
                              detailEntries.push({ label: key, value: String(value) });
                            }
                          });

                          return (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              flexWrap="wrap"
                              alignItems="center"
                              useFlexGap
                            >
                              {/* Method chip */}
                              {method && (
                                <Chip
                                  label={translateMethod(method, t)}
                                  size="small"
                                  color={getMethodColor(method)}
                                  sx={{ fontSize: 11, height: 22 }}
                                />
                              )}

                              {/* User ID with tooltip */}
                              {userId && (
                                <UserTooltipChip
                                  userId={userId}
                                  label={`${t("Bitacora.user_id")}: ${userId}`}
                                  t={t}
                                />
                              )}

                              {/* Modification details in eye icon tooltip */}
                              {isModificationMethod(method) && detailEntries.length > 0 && (
                                <Tooltip
                                  arrow
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        bgcolor: theme.palette.background.paper,
                                        border: `1px solid ${theme.palette.divider}`,
                                        boxShadow: theme.shadows[3],
                                        '& .MuiTooltip-arrow': {
                                          color: theme.palette.background.paper,
                                        },
                                      },
                                    },
                                  }}
                                  title={
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ p: 0.5 }}>
                                      {detailEntries.map((entry, idx) => (
                                        <Tooltip key={idx} title={entry.label} arrow placement="top">
                                          <Chip
                                            label={entry.value}
                                            size="small"
                                            color={getChipColor(entry.label)}
                                            sx={{ fontSize: 10, height: 18, cursor: 'pointer' }}
                                          />
                                        </Tooltip>
                                      ))}
                                    </Stack>
                                  }
                                >
                                  <IconButton size="small">
                                    <OutlineEye size={18} color={theme.palette.primary.main} />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {/* No details fallback */}
                              {!method && !userId && detailEntries.length === 0 && (
                                <Typography variant="body2" color="text.secondary" fontSize={12}>
                                  -
                                </Typography>
                              )}
                            </Stack>
                          );
                        })()
                      : row[col.id]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
};
